// src/App.js
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useState, useEffect, Suspense, lazy } from 'react';
import { AuthClientProvider, useAuthClient } from './core/context/AuthClientContext';
import { CartProvider, useCart } from './core/context/CartContext';

import clientConfig from './client/config/clientConfig';
import useScrollSpy from './core/hooks/useScrollSpy';
import { loadThemeCss, applyCustomTheme6Colors } from './core/utils/theme';

import {
  Header, Hero, About, Menu, Reservation, Contact, Footer
} from './client/components/ThemedComponents';

import CartSidebar from './client/components/CartSidebar';
import OrderForm from './client/components/OrderForm';
import LoginClientModal from './client/components/LoginClientModal';
import RegisterClientModal from './client/components/RegisterClientModal';
import ClientSelector from './client/components/ClientSelector';

import PrivateRoute from './core/routes/PrivateRoute';
import PrivateRouteClient from './core/routes/PrivateRouteClient';

// Pages client
import LoginClient from './client/pages/LoginClient';
import RegisterClient from './client/pages/RegisterClient';
import AccountClient from './client/pages/AccountClient';
import ChangePasswordClient from './client/pages/ChangePasswordClient';
import ResetPasswordRequest from './client/pages/ResetPasswordRequest';
import ResetPassword from './client/pages/ResetPassword';
import OrderSuccess from './client/pages/order-success';
import OrderCancelled from './client/pages/order-cancelled';
import OrderTracker from './client/pages/OrderTracker';

// Pages admin (lazy)
const AdminDashboard   = lazy(() => import('./admin/pages/AdminDashboard'));
const MenuAdmin        = lazy(() => import('./admin/pages/MenuAdmin'));
const ReservationAdmin = lazy(() => import('./admin/pages/ReservationAdmin'));
const MessagesAdmin    = lazy(() => import('./admin/pages/MessagesAdmin'));
const Login            = lazy(() => import('./admin/pages/Login'));
const MenuFFAdmin      = lazy(() => import('./admin/pages/MenuFFAdmin'));
const OrdersAdmin      = lazy(() => import('./admin/pages/OrdersAdmin'));
const ClientAdmin      = lazy(() => import('./admin/pages/ClientAdmin'));
const HeroAdmin        = lazy(() => import('./admin/pages/HeroAdmin'));
const AboutAdmin       = lazy(() => import('./admin/pages/AboutAdmin'));
const SettingsAdmin    = lazy(() => import('./admin/pages/SettingsAdmin'));

// ✅ URL du backend (utilisée partout)
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const Home = ({ theme, scrollOffset }) => {
  const sectionIds = ['home', 'about', 'menu', 'reservation', 'contact'];
  const activeId = useScrollSpy(sectionIds, scrollOffset);

  return (
    <>
      <Header theme={theme} activeId={activeId} />
      <main>
        <Hero theme={theme} />
        <About theme={theme} />
        <Menu theme={theme} />
        <Reservation theme={theme} />
        <Contact theme={theme} />
      </main>
      <Footer theme={theme} />
    </>
  );
};

function AppWrapper() {
  const location = useLocation();
  const [client, setClient] = useState(localStorage.getItem('selectedClient') || clientConfig.theme);
  const [theme, setTheme] = useState(client.replace('_', ''));
  const [themeColors, setThemeColors] = useState(null);

  const isAdminRoute = /^\/(admin|login|menu-admin|reservations-admin|messages-admin|menu-ff-admin|orders-admin|clients-admin)/.test(location.pathname);

  // ✅ Récupère le thème et les couleurs depuis le backend (URL complète, pas relative)
  useEffect(() => {
    async function fetchTheme() {
      try {
        const clientKey = localStorage.getItem('selectedClient') || 'default';
        const res = await fetch(`${API_URL}/api/theme-settings/${clientKey}`);
        if (res.ok) {
          const data = await res.json();
          setTheme(data.theme || 'theme1');
          if (data.theme === 'theme6' && data.colors) {
            setThemeColors(data.colors);
            applyCustomTheme6Colors(data.colors);
          }
        } else {
          loadThemeCss('theme1', isAdminRoute);
        }
      } catch (err) {
        loadThemeCss('theme1', isAdminRoute);
      }
    }
    fetchTheme();
  }, [isAdminRoute]);

  // Appliquer le CSS du thème quand `theme` change
  useEffect(() => {
    loadThemeCss(theme, isAdminRoute);
  }, [theme, isAdminRoute]);

  // Favicon
  useEffect(() => {
    const favicon = document.getElementById('dynamic-favicon');
    const fallbackLogo = '/favicon.png';
    const logoUrl = clientConfig.texts?.nav?.logo || fallbackLogo;
    if (favicon) favicon.href = logoUrl;
  }, []);

  const handleClientChange = (newClient) => {
    localStorage.setItem('selectedClient', newClient);
    setClient(newClient);
  };

  const scrollOffset = theme === 'theme1' ? 100 : 0;

  // --- SEO dynamique (title + OG/Twitter) ---
  useEffect(() => {
    const seo  = clientConfig?.texts?.seo  || {};
    const hero = clientConfig?.texts?.hero || {};

    const title = seo.title || hero.title || 'Le Resto Local';
    const desc  = seo.description || hero.subtitle || 'Restaurant – commande & réservation';
    const img   = seo.image || hero.image || '/cover.jpg';

    document.title = title;

    const setProp = (prop, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', prop);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setName = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setName('description', desc);
    setProp('og:type', 'website');
    setProp('og:title', title);
    setProp('og:description', desc);
    setProp('og:image', img);
    setName('twitter:card', 'summary_large_image');
    setName('twitter:title', title);
    setName('twitter:description', desc);
    setName('twitter:image', img);
  }, [client, theme, location.pathname]);

  // Panier et modals
  const { isCartOpen, cartItems, getTotalPrice, closeCart } = useCart();
  const [isOrderFormOpen, setIsOrderFormOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const { isAuthenticated } = useAuthClient();

  const openOrderForm = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    setIsOrderFormOpen(true);
  };
  const closeOrderForm = () => setIsOrderFormOpen(false);
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    setIsOrderFormOpen(true);
  };

  return (
    <>
      {isCartOpen && (
        <div className="cart-overlay" onClick={closeCart} style={{position:'fixed',top:0,left:0,right:0,bottom:0,backgroundColor:'rgba(0,0,0,0.4)',zIndex:1100,display:'flex',justifyContent:'flex-end'}}>
          <CartSidebar cartItems={cartItems} totalPrice={getTotalPrice()} closeCart={closeCart} openOrderForm={openOrderForm}/>
        </div>
      )}

      {isOrderFormOpen && <OrderForm onClose={closeOrderForm} />}

      {showLoginModal && (
        <LoginClientModal visible onClose={() => setShowLoginModal(false)}
          onSwitchToRegister={() => { setShowLoginModal(false); setShowRegisterModal(true); }}
          onSuccessLogin={handleLoginSuccess}
        />
      )}

      {showRegisterModal && (
        <RegisterClientModal visible onClose={() => setShowRegisterModal(false)}
          onSwitchToLogin={() => { setShowRegisterModal(false); setShowLoginModal(true); }}
        />
      )}

      <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Chargement...</div>}>
        <Routes>
          <Route path="/" element={<Home theme={theme} scrollOffset={scrollOffset} />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
          <Route path="/menu-admin" element={<PrivateRoute><MenuAdmin /></PrivateRoute>} />
          <Route path="/reservations-admin" element={<PrivateRoute><ReservationAdmin /></PrivateRoute>} />
          <Route path="/messages-admin" element={<PrivateRoute><MessagesAdmin /></PrivateRoute>} />
          <Route path="/menu-ff-admin" element={<PrivateRoute><MenuFFAdmin /></PrivateRoute>} />
          <Route path="/orders-admin" element={<PrivateRoute><OrdersAdmin /></PrivateRoute>} />
          <Route path="/clients-admin" element={<PrivateRoute><ClientAdmin /></PrivateRoute>} />
          <Route path="/carousel-admin" element={<PrivateRoute><HeroAdmin /></PrivateRoute>} />
          <Route path="/about-admin" element={<PrivateRoute><AboutAdmin /></PrivateRoute>} />
          <Route path="/settings-admin" element={<PrivateRoute><SettingsAdmin /></PrivateRoute>} />

          <Route path="/client-login" element={<LoginClient />} />
          <Route path="/register" element={<RegisterClient />} />
          <Route path="/account" element={<PrivateRouteClient><AccountClient /></PrivateRouteClient>} />
          <Route path="/change-password" element={<PrivateRouteClient><ChangePasswordClient /></PrivateRouteClient>} />
          <Route path="/reset-password-request" element={<ResetPasswordRequest />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/order-success" element={<OrderSuccess />} />
          <Route path="/order-cancelled" element={<OrderCancelled />} />
          <Route path="/suivi/:orderId" element={<OrderTracker />} />
          <Route path="/orders/track/:orderId" element={<OrderTracker />} />
        </Routes>
      </Suspense>

      {!isAdminRoute && process.env.NODE_ENV === 'development' && (
        <ClientSelector current={client} onChange={handleClientChange} />
      )}
    </>
  );
}

function App() {
  return (
    <Router>
      <CartProvider>
        <AuthClientProvider>
          <AppWrapper />
        </AuthClientProvider>
      </CartProvider>
    </Router>
  );
}

export default App;